import { AuthenticationScheme } from '@see-power-pft-common-js/auth/enums';
import { getPersistStorage } from 'common-react/utils/storage/persist-storage';
import { StateCreator, StoreApi } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import {
  createWithEqualityFn as create,
  UseBoundStoreWithEqualityFn,
} from 'zustand/traditional';

export type AuthState =
  | {
      authenticationScheme: AuthenticationScheme;
      jwtToken: string | undefined;
      codeVerifier: string | undefined;
    }
  | undefined;

export type AuthStoreState = {
  authState: AuthState;
  setAuthState: (authState: AuthState) => void;
};

type AuthStoreCreator = StateCreator<
  AuthStoreState,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  AuthStoreState
>;

const createAuthSlice: AuthStoreCreator = (set) => ({
  authState: undefined,
  setAuthState: (authState: AuthState) => set(() => ({ authState })),
});

export type AuthStoreAccess = {
  useAuthStore: UseBoundStoreWithEqualityFn<StoreApi<AuthStoreState>>;
  useJwtToken: () => string | undefined;
  getJwtToken: () => string | undefined;
  setJwtToken: (token: string | undefined) => void;
  useAuthenticationScheme: () => AuthenticationScheme | undefined;
  getAuthenticationScheme: () => AuthenticationScheme | undefined;
  resetForAuthenticationScheme: (scheme: AuthenticationScheme) => void;
  clearAuthState: () => void;
  getCodeVerifier: () => string | undefined;
  setCodeVerifier: (verifier: string | undefined) => void;
};

const environment = import.meta.env.MODE;

export const getAuthStoreAccess = (appName: string): AuthStoreAccess => {
  const useAuthStore = create<AuthStoreState>()(
    devtools(
      persist(
        (...a) => ({
          ...createAuthSlice(...a),
        }),
        {
          name: 'auth-state',
          storage: createJSONStorage(getPersistStorage),
        },
      ),
      { name: `${appName}-${environment}` },
    ),
  );

  const useJwtToken = () => useAuthStore((state) => state.authState?.jwtToken);

  const getJwtToken = () => useAuthStore.getState().authState?.jwtToken;

  const setJwtToken = (token: string | undefined) => {
    const state = useAuthStore.getState();
    const authState = state.authState;
    if (!authState) {
      throw new Error('Cannot set jwtToken when no auth state (scheme) is set');
    }

    state.setAuthState({
      ...authState,
      jwtToken: token,
    });
  };

  const useAuthenticationScheme = () =>
    useAuthStore((state) => state.authState?.authenticationScheme);

  const getAuthenticationScheme = () =>
    useAuthStore.getState().authState?.authenticationScheme;

  const resetForAuthenticationScheme = (scheme: AuthenticationScheme) =>
    useAuthStore.getState().setAuthState({
      authenticationScheme: scheme,
      jwtToken: undefined,
      codeVerifier: undefined,
    });

  const clearAuthState = () => useAuthStore.getState().setAuthState(undefined);

  const getCodeVerifier = () => useAuthStore.getState().authState?.codeVerifier;

  const setCodeVerifier = (codeVerifier: string | undefined) => {
    const state = useAuthStore.getState();
    const authState = state.authState;
    if (!authState) {
      throw new Error(
        'Cannot set codeVerifier when no auth state (scheme) is set',
      );
    }

    state.setAuthState({
      ...authState,
      codeVerifier: codeVerifier,
    });
  };

  return {
    useAuthStore,
    useJwtToken,
    getJwtToken,
    setJwtToken,
    useAuthenticationScheme,
    getAuthenticationScheme,
    resetForAuthenticationScheme,
    clearAuthState,
    getCodeVerifier,
    setCodeVerifier,
  };
};
